import React, { MouseEvent, useState, useEffect } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String, StringProps } from './String';
import { config } from '../index';

export interface DropDownProps {
  /* DropDown data */
  dropDownProps?: DropDownBlobProps[];
  // --------------------------------------------------------------------------------
  /* DropDown background color */
  backgroundColor?: string;
  /* DropDown border */
  border?: string;
  /* DropDown border radius */
  borderRadius?: string;
  /* DropDown show mode */
  disabled?: boolean;
  /* DropDown width */
  width?: string;
  /* DropDown min width */
  minWidth?: string;
  /* DropDown max width */
  maxWidth?: string;
  /* DropDown max height */
  maxHeight?: string;
  /* DropDown additional class prop */
  className?: string;
  /* DropDown active mode */
  isActive?: boolean;
  /* DropDown placement X & Y */
  transform?: string;
  /* DropDown box shadow */
  boxShadow?: string;
  /* DropDown margin */
  margin?: string;
  /* DropDown padding */
  padding?: string;
  /* DropDown content position */
  position?: 'left' | 'right';
  /* DropDown content font size */
  fontSize?: string;
  /* DropDown zIndex */
  zIndex?: number;
  // --------------------------------------------------------------------------------
  onClick?: (event?: MouseEvent | any) => void;
}
export interface StyledAnimatedWrapper extends DropDownProps {
  height: string | number;
}

export interface DropDownBlobProps {
  title?: StringProps;
  value?: string;
  url?: string;
  iconSize?: string;
  [key: string]: any; // allows for any additional props to be passed
  // --------------------------------------------------------------------------------
  onClick?: (event?: MouseEvent) => void;
}

const StyledWrapper = styled.div<StyledAnimatedWrapper>`
  display: flex;
  position: relative;
  overflow-y: ${(props) => (props.height ? 'unset' : 'hidden')};
  margin: ${(props) => props.margin};
`;

const StyledDropdown = styled.div`
  display: flex;
  flex-direction: column;
  user-select: none;
  width: inherit;
`;

const DropdownWrapper = styled.div<DropDownProps>`
  padding: ${(props) => props.padding};
`;

const StyledAnimatedWrapper = styled.div<StyledAnimatedWrapper>`
  display: flex;
  position: absolute;
  z-index: ${(props) => props.zIndex};
  display: flex;
  overflow: hidden;
  transition: ${config.transitionShort};
  left: ${(props) => (props.position === 'left' ? '0' : 'unset')};
  right: ${(props) => (props.position === 'right' ? '0' : 'unset')};

  max-height: ${(props) => props.maxHeight};
  // covert maxHeight to number for calculation & compare to height. If height < maxHeight, then set on scrollY to maxHeight
  overflow-y: ${(props) =>
    typeof props.maxHeight === 'string' &&
    parseInt(props.maxHeight) < props.height
      ? 'unset'
      : 'hidden'};

  width: ${(props) => props.width};
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  min-width: ${(props) => props.minWidth};
  max-width: ${(props) => props.maxWidth};
  transform: ${(props) =>
    props.disabled ? 'translateY(100%)' : 'translateY(0)'};
  transform: ${(props) => props.transform};
  box-shadow: ${(props) => props.boxShadow};
  // scroll bar styles & config
  &::-webkit-scrollbar {
    width: 8px;
    background-color: ${config.muted};
  }
  &::-webkit-scrollbar-thumb {
    background: ${config.primary};
  }

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  }
  &:active {
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};

    &:after {
      opacity: 1;
      transform: scale(0.95);
    }
  }
`;

const StyledTitleWrapper = styled.div<DropDownProps>`
  display: flex;
  align-items: center;
  padding: ${config.spacingXSmall};
  margin: 0 ${config.spacingSmall};
  transition: ${config.transitionShort};
  border-radius: ${config.spacingSmall}; // initial border radius to avoid transition on first render
  cursor: pointer;

  &:hover {
    background-color: ${config.muted};
  }
`;

const StyledImage = styled.img<DropDownBlobProps>`
  border-radius: 50%;
  overflow: hidden;
  width: ${(props) => props.iconSize};
  height: ${(props) => props.iconSize};
`;

// Primary UI component for user interaction
export const DropDown: React.FC<DropDownProps> = ({
  dropDownProps,
  // --------------------------------------------------------------------------------
  backgroundColor = config.white,
  border = `1px solid ${config.muted}`,
  borderRadius = config.spacingXSmall,
  disabled = false,
  transform = 'translate3d(0,5px,0)', // translate3d(tx, ty, tz)
  width = '100%',
  minWidth = 'fit-content',
  maxWidth = '100%',
  maxHeight = '120px',
  margin,
  padding = `${config.spacingSmall} 0`,
  boxShadow = config.boxShadowLight,
  isActive,
  position = 'left',
  fontSize = config.fontSmall,
  zIndex = 99,
  // --------------------------------------------------------------------------------
  onClick,
  // additional props
  ...props
}) => {
  // --------------------------------------------------------------------------------
  // 📌  Dropdown component for user interaction
  // --------------------------------------------------------------------------------
  const [height, setHeight] = useState<number | string>(0);
  // generate 16 digit random string from date module
  const id = Math.random().toString(36).substring(2, 18);

  useEffect(() => {
    // --------------------------------------------------------------------------------
    // 📌  Apply dropdown toggle & accommodate isActive state change
    // --------------------------------------------------------------------------------

    if (dropDownProps && isActive) {
      // get element with id animated_wrapper height
      const animatedWrapper = document.getElementById(id);
      if (animatedWrapper) {
        let scrollHeight = animatedWrapper.scrollHeight;
        setHeight(scrollHeight);
      } else {
        // default to auto height
        setHeight('auto');
      }
    } else {
      setHeight(0);
    }

    // 👇️ disable the rule for a single line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropDownProps, isActive]);

  // don't render component if title prop not defined
  if (!dropDownProps) return null;

  // --------------------------------------------------------------------------------
  const onClickHandler = (option: DropDownBlobProps) => {
    // --------------------------------------------------------------------------------
    // 📌  Toggle dropdown menu
    // --------------------------------------------------------------------------------

    // close dropdown menu
    setHeight(0);
    // callback passed via props
    onClick?.(option);
    // callbacks passed to via title props
    option.onClick?.();
  };

  return (
    <StyledWrapper height={height} margin={margin}>
      <StyledAnimatedWrapper
        id={id}
        zIndex={zIndex}
        transform={transform}
        width={width}
        maxWidth={maxWidth}
        backgroundColor={backgroundColor}
        border={border}
        borderRadius={borderRadius}
        disabled={disabled}
        minWidth={minWidth}
        maxHeight={maxHeight}
        boxShadow={boxShadow}
        height={height} // set height from isActive state change
        position={position}
        style={{ height }}
        // --------------------------------------------------------------------------------
        {...props}
      >
        <StyledDropdown>
          <DropdownWrapper padding={padding}>
            {dropDownProps?.map((option, index) => {
              // break if no string Provided
              if (!option?.title?.string) return null;
              const url = option?.url ?? '';
              const iconSize = option.iconSize ?? config.spacingXMedium;

              return (
                <StyledTitleWrapper
                  key={index}
                  onClick={() => {
                    onClickHandler(option);
                  }}
                >
                  {url && (
                    <StyledImage
                      src={url}
                      alt={option.title?.string}
                      iconSize={iconSize}
                      loading="lazy"
                    />
                  )}

                  <String
                    fontSize={fontSize}
                    fontWeight="700"
                    string={option.title?.string}
                    color={config.darkMuted}
                    limit={1}
                    cursor="pointer"
                    margin={`0 ${config.spacingSmall}`}
                    // additional props
                    {...option.title}
                  />
                </StyledTitleWrapper>
              );
            })}
          </DropdownWrapper>
        </StyledDropdown>
      </StyledAnimatedWrapper>
    </StyledWrapper>
  );
};
